import React, { useState } from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useRouter } from "next/router";
import get from "lodash/get";
import cn from "classnames";
import toSafeInteger from "lodash/toSafeInteger";
import { LEASE_TYPE, EMAIL_REGEX } from "util/constants";

import { track, eventCategories, analyticsEvents } from "util/analytics";
import { trackAccountCreate } from "util/ga4";
import useToast from "hooks/useToast";
import { useCurrentUser } from "hooks";
import { getCookie } from "util/cookies";
import getTrackingCookie from "util/adwords";
import SignInModal from "components/componentLibrary/Modals/SignInModal";
import {
  CreateRenterSearchActiveRequirementsMutation,
  determineLeaseTerm,
} from "components/ClientSearch/Filters/SaveActiveRequirementsButton";

import Modal from "components/componentLibrary/Modals/SplitContentModal";
import styles from "./styles.module.scss";

const ListingImageQuery = gql`
  query ListingImageQuery($id: ID!) {
    listing(id: $id) {
      id
      image2 {
        id
        url(size: "slideshow_big")
      }
      address
      floorAndSuite
      building {
        id
      }
    }
  }
`;

const RENTER_ONBOARDING_MUTATION = gql`
  mutation RenterOnboardingSignUpMutation(
    $email: String!
    $phone: String
    $cityOfInterest: CityName
    $marketOfInterest: ID
    $numberOfEmployees: Int
    $squareFeet: Int
    $neighborhoodIds: [ID]
    $neighborhoodNames: [String]
    $signUpType: String
    $source: String
    $areaExternalIds: [ID]
  ) {
    renterOnboarding(
      input: {
        renter: { phone: $phone, email: $email, signUpType: $signUpType }
        cityOfInterest: $cityOfInterest
        marketOfInterest: $marketOfInterest
        areaExternalIds: $areaExternalIds
        onboardingInfo: {
          source: $source
          neighborhoodIds: $neighborhoodIds
          numberOfEmployees: $numberOfEmployees
          squareFeet: $squareFeet
          neighborhoodNames: $neighborhoodNames
        }
      }
    ) {
      onboardingId
      created
      neighborhoodNames
      currentUser {
        id
        uuid
        email
        phone
        isAdmin
        lastName
        isRenter
        isBroker
        firstName
        e164Phone
        isVerified
        companyName
        displayName
        hasPassword
        salesforceId
        featureFlags
        profilePicURL
        completedUserTasks {
          id
          task
        }
        confirmed
        cityOfInterest
        unconfirmedEmail
        tours {
          id
          date
        }
        latestCampaign {
          id
          uuid
          listings {
            id
          }
          currentActiveRequirements(setByUser: true) {
            id
            neighborhoods {
              id
              name
            }
            minPsf
            maxPsf
            minSquareFeet
            maxSquareFeet
            minMonthlyBudget
            maxMonthlyBudget
            numberOfEmployees
            minSqft: minSquareFeet
            maxSqft: maxSquareFeet
            maxPricePerSquareFoot: maxPsf
            minPricePerSquareFoot: minPsf
            maxMonthlyPrice: maxMonthlyBudget
            minMonthlyPrice: minMonthlyBudget
            leaseTerm
            areas {
              id
              externalId
              name
            }
            market {
              id
              name
              externalId
              position {
                lat
                lng
              }
            }
          }
        }
      }
      errors {
        auth
      }
    }
  }
`;

const ADD_INQUIRY_MUTATION = gql`
  mutation AddInquiryMutation(
    $email: String!
    $inquiribleId: Int!
    $source: String
    $gclid: String
    $serverId: String
    $googleAnalyticsId: String
    $signUpType: String
    $availableTimes: [AvailableTimes]
  ) {
    addInquiry(
      input: {
        renter: {
          email: $email
          serverId: $serverId
          gclid: $gclid
          signUpType: $signUpType
          googleAnalyticsId: $googleAnalyticsId
        }
        inquiribleId: $inquiribleId
        onboardingInfo: { source: $source }
        availableTimes: $availableTimes
      }
    ) {
      errors {
        email
      }
      created
      currentUser {
        id
        uuid
        email
        phone
        isAdmin
        lastName
        isRenter
        isBroker
        firstName
        e164Phone
        isVerified
        companyName
        displayName
        hasPassword
        salesforceId
        featureFlags
        profilePicURL
        completedUserTasks {
          id
          task
        }
        confirmed
        cityOfInterest
        unconfirmedEmail
        tours {
          id
          date
        }
        latestCampaign {
          id
          uuid
          listings {
            id
          }
          currentActiveRequirements(setByUser: true) {
            id
            neighborhoods {
              id
              name
            }
            minPsf
            maxPsf
            minSquareFeet
            maxSquareFeet
            minMonthlyBudget
            maxMonthlyBudget
            numberOfEmployees
            minSqft: minSquareFeet
            maxSqft: maxSquareFeet
            maxPricePerSquareFoot: maxPsf
            minPricePerSquareFoot: minPsf
            maxMonthlyPrice: maxMonthlyBudget
            minMonthlyPrice: minMonthlyBudget
            leaseTerm
            areas {
              id
              externalId
              name
            }
            market {
              id
              name
              externalId
              position {
                lat
                lng
              }
            }
          }
        }
      }
    }
  }
`;

const getSideImage = (listing) => {
  if (!listing) return "";
  return get(listing, ["image2", "url"], "");
};

const SideContent = ({ listing }) => {
  if (!listing) return <></>;
  return (
    <div
      className={cn(styles.sideContent, styles.sideContentContextual)}
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(0, 0, 0, 0.5)), url(${getSideImage(
          listing
        )})`,
      }}
    >
      <p>{listing?.address || ""}</p>
      {" - "}
      <p>{listing?.floorAndSuite}</p>
    </div>
  );
};

SideContent.propTypes = {
  listing: PropTypes.shape({
    address: PropTypes.string,
    floorAndSuite: PropTypes.string,
  }),
};
SideContent.defaultProps = { listing: null };

const ContextualSignUpModal = ({
  inquiribleId,
  onClose,
  source,
  signUpType,
  type,
  areaExternalIds,
  marketOfInterest,
  cityOfInterest,
  leaseTermMin,
  leaseTermMax,
  minPricePerSquareFoot,
  maxPricePerSquareFoot,
  minSqft,
  maxSqft,
  minMonthlyPrice,
  maxMonthlyPrice,
  numEmployees,
  phoneRequired,
  onSuccess,
  availableTimes,
}) => {
  const [addInquiry] = useMutation(ADD_INQUIRY_MUTATION);
  const [onboardRenter] = useMutation(RENTER_ONBOARDING_MUTATION);
  const [createRenterSearchActiveRequirements] = useMutation(
    CreateRenterSearchActiveRequirementsMutation
  );
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [visitSpace, setVisitSpace] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [existingEmail, setExistingEmail] = useState("");
  const router = useRouter();
  const { showToast } = useToast();
  const { currentUser, refreshCurrentUser } = useCurrentUser();

  const { data, loading } = useQuery(ListingImageQuery, {
    fetchPolicy: "cache-first",
    variables: {
      id: inquiribleId,
    },
    skip: type === "Building",
  });

  const handleClose = () => {
    track(analyticsEvents.close, {
      category: eventCategories.modal,
      label: "Save Listing",
    });
    onClose();
  };

  const sendInquiry = async () => {
    const variables = {
      email,
      phone,
      gclid: getTrackingCookie(),
      signUpType: `${type} Inquiry`,
      source,
      serverId: getCookie("SERVERID") || "unidentified",
      googleAnalyticsId: getCookie("_gid"),
      inquiribleId: parseInt(inquiribleId, 10),
      availableTimes: availableTimes.length === 0 ? null : availableTimes,
    };
    if (visitSpace && availableTimes.length === 0) {
      variables.availableTimes = ["ANY_TIME_AND_DAY_WORKS"];
    }
    const { data: result } = await addInquiry({ variables });
    if (result?.addInquiry?.errors?.length) {
      router.push("/sign-in");
      return;
    }
    const toastMessage = visitSpace
      ? `${type} saved! A broker will be in contact soon to arrange a tour time.`
      : `${type} saved! You can view saved ${type.toLowerCase()}s in your tourbook.`;
    showToast(toastMessage, 5000);
    refreshCurrentUser();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const onboardingVariables = {
      email,
      phone,
      marketOfInterest,
      cityOfInterest,
      areaExternalIds,
      signUpType,
      source: visitSpace ? `${source} - Tour Request` : source,
    };

    const { data: renterObResult } = await onboardRenter({
      variables: onboardingVariables,
    });
    if (!renterObResult?.renterOnboarding?.created) {
      setExistingEmail(email);
      setSaving(false);
      setShowSignInModal(true);
      return;
    }
    if (renterObResult?.renterOnboarding?.errors?.length) {
      router.push("/sign-in");
      return;
    }

    track(analyticsEvents.modal_sign_up, {
      category: eventCategories.account,
      label: `Sign up from ${router.pathname}${type === "Listing" && inquiribleId ? `, listing: ${inquiribleId}` : ""
        }`,
    });
    trackAccountCreate({
      sourcePage: window.location.pathname,
      listingId: inquiribleId || undefined,
      city: cityOfInterest || undefined,
    });

    await refreshCurrentUser();

    const leaseTerm = determineLeaseTerm({
      leaseTermMin,
      leaseTermMax,
    });
    await createRenterSearchActiveRequirements({
      variables: {
        marketExternalId: marketOfInterest,
        leaseTerm,
        statuses: [
          "available",
          "offers_in",
          "squarefoot_offers_in",
          "lease_out",
          "squarefoot_lease_out",
        ],
        leaseTypes: [...Object.keys(LEASE_TYPE), "UNKNOWN"],
        campaignId:
          renterObResult?.renterOnboarding?.currentUser.latestCampaign.id,
        minPricePerSquareFoot,
        maxPricePerSquareFoot,
        minSquareFeet: minSqft,
        maxSquareFeet: maxSqft,
        minMonthlyBudget: minMonthlyPrice,
        maxMonthlyBudget: maxMonthlyPrice,
        numberOfEmployees: toSafeInteger(numEmployees),
        areaExternalIds,
      },
    });
    if (inquiribleId) {
      sendInquiry();
    }
    onSuccess(visitSpace);
    onClose();
  };

  const trackEvent = () =>
    track(analyticsEvents.sign_in_link, {
      category: eventCategories.account,
      label: "",
    });

  const signInOnClose = () => {
    if (!currentUser) {
      onClose();
      return;
    }
    const redirectUrl = `/tourbook/search${router.search}`;
    router.push(redirectUrl);
    onClose();
  };

  if (showSignInModal)
    return (
      <SignInModal
        existingEmail={existingEmail}
        onClose={signInOnClose}
        onSuccess={() => {
          if (inquiribleId) sendInquiry();
          onClose();
          showToast("You are now signed in");
        }}
        customSideContent={
          type === "Listing" ? (
            <SideContent listing={data?.listing} />
          ) : undefined
        }
      />
    );

  return (
    <Modal
      sideContent={
        type === "Listing" && <SideContent listing={data?.listing} />
      }
      onClose={handleClose}
    >
      <h1 className={styles.heading}>Create Account</h1>
      <h2 className={styles.subheading}>
        Create a SquareFoot account to save listings
      </h2>

      <form
        data-qa="sign-up-form"
        className={styles.form}
        onSubmit={handleSubmit}
      >
        {availableTimes.length === 0 && (
          <div className={styles.checkboxContainer}>
            <label htmlFor="visit-space" className={styles.checkboxLabel}>
              <input
                type="checkbox"
                id="visit-space"
                value={visitSpace}
                onChange={() => setVisitSpace(!visitSpace)}
              />
              I want to visit this space
            </label>
          </div>
        )}

        <input
          value={email}
          onChange={({ target }) => setEmail(target.value)}
          placeholder="Email"
          className={styles.input}
          type="email"
          pattern={EMAIL_REGEX}
          title={`${email} is not a valid email`}
          data-qa="sign-up-modal-email"
          required
        />

        <input
          type="tel"
          pattern="^[ ()+*#\-]*(?:\d[ ()+*#\-]*){7,40}$"
          title="Please enter a valid phone number"
          maxLength="40"
          className={styles.input}
          value={phone}
          placeholder={
            visitSpace || phoneRequired ? "Phone" : "Phone (optional)"
          }
          onChange={({ target }) => setPhone(target.value)}
          data-qa="sign-up-modal-phone"
          required={visitSpace}
        />
        <button
          type="submit"
          className={styles.button}
          disabled={
            loading ||
            !email ||
            saving ||
            ((visitSpace || !!phoneRequired) && !phone)
          }
          data-qa="sign-up-button"
        >
          {saving ? "SIGNING UP" : "SIGN UP"}
        </button>
      </form>

      <p>
        Already have an account?{" "}
        <span
          role="button"
          tabIndex="0"
          className={styles.link}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setShowSignInModal(true);
              trackEvent();
            }
          }}
          onClick={() => {
            setShowSignInModal(true);
            trackEvent();
          }}
        >
          Sign In
        </span>
      </p>
    </Modal>
  );
};

ContextualSignUpModal.propTypes = {
  inquiribleId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  source: PropTypes.string,
  signUpType: PropTypes.string,
  type: PropTypes.oneOf(["Listing", "Building"]),
  availableTimes: PropTypes.arrayOf(PropTypes.string),
  areaExternalIds: PropTypes.arrayOf(PropTypes.string),
  marketOfInterest: PropTypes.string,
  cityOfInterest: PropTypes.string,
  leaseTermMin: PropTypes.number,
  leaseTermMax: PropTypes.number,
  minPricePerSquareFoot: PropTypes.number,
  maxPricePerSquareFoot: PropTypes.number,
  minSqft: PropTypes.number,
  maxSqft: PropTypes.number,
  minMonthlyPrice: PropTypes.number,
  maxMonthlyPrice: PropTypes.number,
  numEmployees: PropTypes.string,
  phoneRequired: PropTypes.bool,
  onSuccess: PropTypes.func,
};

ContextualSignUpModal.defaultProps = {
  source: "unknown",
  signUpType: null,
  type: "Listing",
  availableTimes: [],
  areaExternalIds: [],
  marketOfInterest: null,
  cityOfInterest: null,
  leaseTermMin: null,
  leaseTermMax: null,
  minPricePerSquareFoot: null,
  maxPricePerSquareFoot: null,
  minSqft: null,
  maxSqft: null,
  minMonthlyPrice: null,
  maxMonthlyPrice: null,
  numEmployees: null,
  phoneRequired: false,
  // eslint-disable-next-line no-unused-vars
  onSuccess: (visitSpace) => { },
};

export default ContextualSignUpModal;
